/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  Change,
  GetServerIdentityImpactRequest,
  GetServerIdentityImpactSecurity,
} from "../models/operations/getserveridentityimpact.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildServerIdentityImpactQuery,
  prefetchServerIdentityImpact,
  queryKeyServerIdentityImpact,
  ServerIdentityImpactQueryData,
} from "./serverIdentityImpact.core.js";
export {
  buildServerIdentityImpactQuery,
  prefetchServerIdentityImpact,
  queryKeyServerIdentityImpact,
  type ServerIdentityImpactQueryData,
};

export type ServerIdentityImpactQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getServerIdentityImpact remoteSessions
 *
 * @remarks
 * Preview which other MCP servers and gateways a client binding change on a user session issuer would affect, across every project in the organization for an organization-level issuer. Each server's upstream authorization server is derived from the clients it can see (its own project's and organization-level ones), so a server is listed when that derivation changes (repoint or clear) or when a client it uses is replaced (resignin). A gateway is listed when a client it can see is unbound (client_removed). The target server is excluded. A change the commit would refuse, such as one that touches an organization-level client on an organization-level issuer, is refused here too. Requires mcp:write on mcp_server_id when given, otherwise project:write. Servers the caller cannot read are counted in hidden_server_count and never named.
 */
export function useServerIdentityImpact(
  request: GetServerIdentityImpactRequest,
  security?: GetServerIdentityImpactSecurity | undefined,
  options?: QueryHookOptions<
    ServerIdentityImpactQueryData,
    ServerIdentityImpactQueryError
  >,
): UseQueryResult<
  ServerIdentityImpactQueryData,
  ServerIdentityImpactQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildServerIdentityImpactQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getServerIdentityImpact remoteSessions
 *
 * @remarks
 * Preview which other MCP servers and gateways a client binding change on a user session issuer would affect, across every project in the organization for an organization-level issuer. Each server's upstream authorization server is derived from the clients it can see (its own project's and organization-level ones), so a server is listed when that derivation changes (repoint or clear) or when a client it uses is replaced (resignin). A gateway is listed when a client it can see is unbound (client_removed). The target server is excluded. A change the commit would refuse, such as one that touches an organization-level client on an organization-level issuer, is refused here too. Requires mcp:write on mcp_server_id when given, otherwise project:write. Servers the caller cannot read are counted in hidden_server_count and never named.
 */
export function useServerIdentityImpactSuspense(
  request: GetServerIdentityImpactRequest,
  security?: GetServerIdentityImpactSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    ServerIdentityImpactQueryData,
    ServerIdentityImpactQueryError
  >,
): UseSuspenseQueryResult<
  ServerIdentityImpactQueryData,
  ServerIdentityImpactQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildServerIdentityImpactQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setServerIdentityImpactData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      userSessionIssuerId: string;
      mcpServerId?: string | undefined;
      change: Change;
      providerId?: string | undefined;
      clientId?: string | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: ServerIdentityImpactQueryData,
): ServerIdentityImpactQueryData | undefined {
  const key = queryKeyServerIdentityImpact(...queryKeyBase);

  return client.setQueryData<ServerIdentityImpactQueryData>(key, data);
}

export function invalidateServerIdentityImpact(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      userSessionIssuerId: string;
      mcpServerId?: string | undefined;
      change: Change;
      providerId?: string | undefined;
      clientId?: string | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "remoteSessions",
      "getServerIdentityImpact",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllServerIdentityImpact(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "remoteSessions", "getServerIdentityImpact"],
  });
}
